// packages/frontend/src/contexts/AuthContext.tsx

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { auth } from '../services/firebase';
import type { User, Therapist } from '@msb/shared';
import { userFetchService } from '../services/user-fetch-service';
import { createLogger } from '../utils/logger';
import { posthogService } from '../services/posthogService';
import { updateGlobalAuthState } from '../stores/authContextBridge';
import { clearStoredData } from '../services/tokenService';
import { checkEmailVerified as checkEmailVerifiedService } from '../services/authService';
import { idConsistencyService } from '../services/idConsistencyService';
import { startTokenExpirationMonitor } from '../services/tokenExpirationMonitor';
import { LogoutDialog } from '../components/auth/LogoutDialog';
import { convertFirebaseUserToMSBUser } from '../services/firebaseUserMapping';
import { applyPendingProfile } from '../services/pendingProfile';

const logger = createLogger('AuthContext');

// Firebase reloads emailVerified from the server when it restores a session, but
// the ID token the callables send can still carry the older email_verified claim
// (the gate reads the claim, which only changes on a token mint). Refresh it so a
// verified account is not refused by the gate on the first load.
const refreshIdTokenForVerifiedUser = async (fbUser: FirebaseUser): Promise<void> => {
  try {
    await fbUser.getIdToken(true);
  } catch (err) {
    logger.warn('Could not refresh ID token before loading user data', {
      error: err instanceof Error ? err.message : 'Unknown error',
    });
  }
};

interface AuthContextType {
  // Core state
  firebaseUser: FirebaseUser | null;
  currentUser: User | null;
  currentTherapist: Therapist | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;

  // Email verification
  emailVerified: boolean;
  refreshEmailVerified: () => Promise<boolean>;

  // Actions
  signOut: (options?: { redirectToLogin?: boolean; reason?: string }) => Promise<void>;
  getToken: () => Promise<string | null>;
  refreshUserData: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  // State - Start with loading = true (critical for preventing UI flash)
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [currentTherapist, setCurrentTherapist] = useState<Therapist | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [emailVerified, setEmailVerified] = useState(false);

  // Firebase does not await onAuthStateChanged observers, so a later sign-out
  // or sign-in can land while an earlier fetchUserData call is still in
  // flight. Each auth event bumps this epoch; a fetch whose epoch no longer
  // matches when it resolves is stale and must not write state.
  const authEpochRef = useRef(0);

  // Fetch user data from Firestore
  const fetchUserData = useCallback(async (fbUser: FirebaseUser, epoch: number): Promise<void> => {
    try {
      logger.info('Fetching user data from Firestore', {
        firebaseUid: fbUser.uid,
        email: fbUser.email,
      });

      const msbUser = await userFetchService.fetchUserWithRetry(fbUser.uid, 3, 500);

      if (authEpochRef.current !== epoch) {
        logger.info('Discarding stale user data fetch; a newer auth event has already landed', {
          firebaseUid: fbUser.uid,
        });
        return;
      }

      if (msbUser) {
        // Verified account: apply the names held from registration, if any.
        const loadedUser = await applyPendingProfile(fbUser.uid, msbUser);
        const therapist = loadedUser.userType === 'therapist' ? (loadedUser as Therapist) : null;

        setCurrentUser(loadedUser);
        setCurrentTherapist(therapist);
        setIsAuthenticated(true);
        setError(null);

        logger.info('User data loaded successfully', {
          userId: msbUser.id,
          userType: msbUser.userType,
          isTherapist: !!therapist,
        });

        // Set analytics identity
        if (therapist) {
          posthogService.setUserType('therapist');
          posthogService.identifyTherapist(therapist).catch((err) => {
            logger.warn('Failed to identify therapist in PostHog', {
              error: err instanceof Error ? err.message : 'Unknown error',
              therapistId: therapist.id,
            });
          });
        }
      } else {
        logger.warn('Failed to load user data from Firestore', {
          firebaseUid: fbUser.uid,
        });
        setError('Failed to load user data');
      }
    } catch (err) {
      if (authEpochRef.current !== epoch) {
        return;
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to load user data';
      logger.error('Error fetching user data', {
        error: errorMessage,
        firebaseUid: fbUser.uid,
      });
      setError(errorMessage);
    }
  }, []);

  // Refresh user data
  const refreshUserData = useCallback(async (): Promise<void> => {
    if (!firebaseUser) {
      logger.warn('Cannot refresh user data: no Firebase user');
      return;
    }

    await fetchUserData(firebaseUser, authEpochRef.current);
  }, [firebaseUser, fetchUserData]);

  // Get authentication token
  const getToken = useCallback(async (): Promise<string | null> => {
    if (!firebaseUser) {
      return null;
    }

    try {
      const token = await firebaseUser.getIdToken();
      return token;
    } catch (err) {
      logger.error('Failed to get ID token', {
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      return null;
    }
  }, [firebaseUser]);

  // Sign out
  const signOut = useCallback(async (options?: { redirectToLogin?: boolean; reason?: string }): Promise<void> => {
    try {
      logger.info('Signing out user', {
        currentUserId: currentUser?.id,
        isAuthenticated,
        reason: options?.reason,
      });

      // Clear analytics identity first (before clearing state)
      try {
        posthogService.setUserType('anonymous');
        posthogService.reset();
      } catch (analyticsError) {
        logger.warn('Failed to clear analytics identity', {
          error: analyticsError instanceof Error ? analyticsError.message : 'Unknown error',
        });
      }

      // Sign out from Firebase - this will trigger onAuthStateChanged with null
      await firebaseSignOut(auth);

      // Clear token service storage (firebase_auth_token, firebase_user_data, etc.)
      try {
        clearStoredData();
      } catch (tokenError) {
        logger.warn('Failed to clear token service storage', {
          error: tokenError instanceof Error ? tokenError.message : 'Unknown error',
        });
      }

      // Clear any old Zustand persist storage (in case it still exists)
      try {
        localStorage.removeItem('msb-auth-store');
        localStorage.removeItem('msb-enhanced-auth-store');
        localStorage.removeItem('msb-firebase-auth-store');
      } catch (storageError) {
        logger.warn('Failed to clear localStorage', {
          error: storageError instanceof Error ? storageError.message : 'Unknown error',
        });
      }

      logger.info('Sign out successful');

      // Redirect to login if requested (e.g., for auto-logout on token expiration)
      if (options?.redirectToLogin) {
        const loginUrl = '/login';
        const reason = options.reason ? `?reason=${encodeURIComponent(options.reason)}` : '';
        window.location.href = `${loginUrl}${reason}`;
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Sign out failed';
      logger.error('Sign out failed', { error: errorMessage });
      setError(errorMessage);
      throw err;
    }
  }, [currentUser, isAuthenticated]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reload the Firebase user and force a token refresh so `email_verified`
  // reflects the latest state, then update local state to match.
  const refreshEmailVerified = useCallback(async (): Promise<boolean> => {
    try {
      const verified = await checkEmailVerifiedService();
      // Verified just now: load the user document before routes unblock, which
      // also applies any pending registration profile.
      if (verified && !emailVerified && firebaseUser) {
        await fetchUserData(firebaseUser, authEpochRef.current);
      }
      setEmailVerified(verified);
      return verified;
    } catch (err) {
      logger.error('Failed to refresh email verification status', {
        error: err instanceof Error ? err.message : 'Unknown error',
      });
      return emailVerified;
    }
  }, [emailVerified, firebaseUser, fetchUserData]);

  // Sync state with global bridge (for backward compatibility with old stores)
  useEffect(() => {
    updateGlobalAuthState({
      firebaseUser,
      currentUser,
      currentTherapist,
      isAuthenticated,
      loading,
      error,
    });
  }, [firebaseUser, currentUser, currentTherapist, isAuthenticated, loading, error]);

  // Set up Firebase auth state listener - THE SINGLE SOURCE OF TRUTH
  useEffect(() => {
    logger.info('Setting up auth state listener');

    const unsubscribe = onAuthStateChanged(auth, async (fbUser) => {
      const epoch = ++authEpochRef.current;
      logger.info('Auth state changed', {
        hasUser: !!fbUser,
        firebaseUid: fbUser?.uid,
        email: fbUser?.email,
      });

      if (fbUser) {
        // User signed in. If a different user is now signed in, drop any cached
        // therapist/client IDs from the prior session so they are not reused.
        const cachedTherapistId = idConsistencyService.getCacheStatus().therapistId;
        if (cachedTherapistId && cachedTherapistId !== fbUser.uid) {
          idConsistencyService.clearCache();
        }

        setFirebaseUser(fbUser);

        const verified = fbUser.emailVerified;
        if (verified) {
          await refreshIdTokenForVerifiedUser(fbUser);
        }
        if (authEpochRef.current !== epoch) {
          // A newer auth event has landed; it sets the state and loading flag.
          return;
        }
        setEmailVerified(verified);

        if (verified) {
          // Fetch full user data from Firestore
          await fetchUserData(fbUser, epoch);
        } else {
          logger.info('Email not verified; signed in from Firebase Auth data, user callables skipped');
          const fallbackUser = convertFirebaseUserToMSBUser(fbUser);
          setCurrentUser(fallbackUser);
          setCurrentTherapist(fallbackUser.userType === 'therapist' ? (fallbackUser as Therapist) : null);
          setIsAuthenticated(true);
          setError(null);
        }
      } else {
        // User signed out - clear all state
        logger.info('User signed out, clearing state');
        idConsistencyService.clearCache();
        setFirebaseUser(null);
        setCurrentUser(null);
        setCurrentTherapist(null);
        setIsAuthenticated(false);
        setEmailVerified(false);
        setError(null);
      }

      // Auth check complete
      setLoading(false);
    });

    // Cleanup listener on unmount
    return () => {
      logger.info('Cleaning up auth state listener');
      unsubscribe();
    };
  }, [fetchUserData]);

  // Set up token expiration monitor
  useEffect(() => {
    if (isAuthenticated && firebaseUser) {
      logger.info('Starting token expiration monitor');

      const cleanup = startTokenExpirationMonitor({
        onExpiration: async () => {
          logger.warn('Token expired, logging out user');
          await signOut({ redirectToLogin: true, reason: 'expired' });
        },
        onExpiringSoon: (minutesRemaining: number) => {
          logger.info('Token expiring soon', { minutesRemaining });
          // Optional: Show warning notification to user
          // This can be enhanced with a toast notification component
        },
      });

      return cleanup;
    }
  }, [isAuthenticated, firebaseUser, signOut]);

  const value: AuthContextType = {
    firebaseUser,
    currentUser,
    currentTherapist,
    isAuthenticated,
    loading,
    error,
    emailVerified,
    refreshEmailVerified,
    signOut,
    getToken,
    refreshUserData,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      <LogoutDialog />
      {!loading && children}
    </AuthContext.Provider>
  );
}

